// Слои карты отчёта о пропусках (чистые GeoJSON): точки судов их цветами,
// пропуск — пунктир от точки «до» к точке «после» с номером, круг радиуса
// соседей у пропуска в море, точки соседей с сигналом — одним цветом.
//
// Долгота за 180° приводится к концу «до»: пунктир Берингова моря иначе
// прошёл бы через весь глобус.

import type { AisGap, AisGapResult } from '../api'

type FC = GeoJSON.FeatureCollection

const fc = (features: GeoJSON.Feature[]): FC => ({ type: 'FeatureCollection', features })

/** Долгота `lon` в обороте, ближайшем к `ref`. */
export function nearLon(lon: number, ref: number): number {
  let out = lon
  while (out - ref > 180) out -= 360
  while (ref - out > 180) out += 360
  return out
}

export function vesselPoints(result: AisGapResult, colors: Map<number, string>): FC {
  return fc(result.vessels.flatMap(v => v.track.map(([t, lat, lon]) => ({
    type: 'Feature' as const,
    properties: { mmsi: v.mmsi, color: colors.get(v.mmsi) ?? '#fff', t },
    geometry: { type: 'Point' as const, coordinates: [lon, lat] },
  }))))
}

export function neighborPoints(result: AisGapResult): FC {
  return fc(Object.entries(result.neighbors).flatMap(([mmsi, nb]) => nb.track.map(([t, lat, lon]) => ({
    type: 'Feature' as const,
    properties: { mmsi: Number(mmsi), t },
    geometry: { type: 'Point' as const, coordinates: [lon, lat] },
  }))))
}

/** Пунктир пропуска (оба конца известны) и номер у середины или у известного конца. */
export function gapLines(result: AisGapResult): { lines: FC; labels: FC } {
  const lines: GeoJSON.Feature[] = []
  const labels: GeoJSON.Feature[] = []
  for (const g of result.gaps) {
    const ends = [g.before, g.after].filter((e): e is [number, number] => e != null)
    if (!ends.length) continue
    const props = { no: g.no, port: g.port != null }
    const ref = ends[0][1]
    const pts = ends.map(([lat, lon]) => [nearLon(lon, ref), lat])
    if (pts.length === 2) {
      lines.push({ type: 'Feature', properties: props, geometry: { type: 'LineString', coordinates: pts } })
    }
    const mid = pts.length === 2 ? [(pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2] : pts[0]
    labels.push({ type: 'Feature', properties: props, geometry: { type: 'Point', coordinates: mid } })
  }
  return { lines: fc(lines), labels: fc(labels) }
}

/** Круг `km` вокруг точки — многоугольником (радиус по долготе делится на cos широты). */
export function circle(lat: number, lon: number, km: number, n = 48): number[][] {
  const dlat = km / 111.32
  const dlon = dlat / Math.max(0.05, Math.cos((lat * Math.PI) / 180))
  const ring: number[][] = []
  for (let i = 0; i <= n; i++) {
    const a = (2 * Math.PI * i) / n
    ring.push([lon + dlon * Math.cos(a), lat + dlat * Math.sin(a)])
  }
  return ring
}

/** Круги радиуса соседей у концов пропусков в море (у порта — нет: там норма). */
export function gapCircles(result: AisGapResult): FC {
  const features: GeoJSON.Feature[] = []
  for (const g of result.gaps as AisGap[]) {
    if (g.port) continue
    for (const end of [g.before, g.after]) {
      if (!end) continue
      features.push({ type: 'Feature', properties: { no: g.no, verdict: g.check?.verdict ?? 'unchecked' },
                      geometry: { type: 'Polygon', coordinates: [circle(end[0], end[1], result.radius_km)] } })
    }
  }
  return fc(features)
}

/** Рамка всех точек судов `[[запад, юг], [восток, север]]`; через 180° — в восточной шкале. */
export function dataBounds(result: AisGapResult): [[number, number], [number, number]] | null {
  const pts = result.vessels.flatMap(v => v.track.map(p => [p[2], p[1]]))
  if (!pts.length) return null
  const lons = pts.map(p => p[0])
  const wrap = Math.max(...lons) - Math.min(...lons) > 180
  const ls = wrap ? lons.map(l => (l < 0 ? l + 360 : l)) : lons
  const lats = pts.map(p => p[1])
  return [[Math.min(...ls), Math.min(...lats)], [Math.max(...ls), Math.max(...lats)]]
}
